import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import {
    useCompleteCoSubscriptionSigningSessionMutation,
    useCompleteSubscriptionSigningSessionMutation,
    useGetSubscriptionSigningSessionMutation,
} from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { getUserIdFromIri, setTunnelChangesHandlers } from 'utils/helpers';
import { useSubscriptionFormSignatureStatus, useYousign } from 'utils/hooks';
import { stepStatus } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { Loader, SubmitButton } from 'components';
import { Box, Typography } from '@mui/material';

const iframeId = 'bs-iframe';

const SubscriptionFormSignature = props => {
    const { userStep, handleToggle, mainSubscriberHasSigned } = props;

    const [getSubscriptionSigningSession, { isLoading: sessionLoader, isUninitialized }] =
        useGetSubscriptionSigningSessionMutation();
    const [completeSubscriptionSigningSession, { isSuccess: completeSuccess }] =
        useCompleteSubscriptionSigningSessionMutation();
    const [completeCoSubscriptionSigningSession, { isSuccess: coSubscriberSuccess }] =
        useCompleteCoSubscriptionSigningSessionMutation();

    const dispatch = useDispatch();
    const internalUser = useSelector(userSelectors.getInternalUser);
    const loggingOut = useSelector(userSelectors.getLogoutLoader);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);

    const subscriberHasSigned = useSubscriptionFormSignatureStatus(tunnelSubscription); // both need to have signed for co-subscriptions
    const signatureProcedureInitiated = useMemo(() => userStep?.signatures?.length > 0, [userStep]);

    const currentSignature = useMemo(() => {
        const mainSubscriberSignature = userStep?.signatures?.find(
            s => s.signatoryUser === tunnelSubscription?.user
        );

        if (tunnelSubscription?.withCoSubscriber) {
            if (mainSubscriberHasSigned) {
                return userStep?.signatures?.find(
                    s => getUserIdFromIri(s.signatoryUser) === tunnelSubscription?.coSubscriber?.id
                );
            } else {
                return mainSubscriberSignature;
            }
        } else {
            return mainSubscriberSignature;
        }
    }, [mainSubscriberHasSigned, userStep, tunnelSubscription]);

    const [frameError, setFrameError] = useState(false);
    const [cancelled, setCancelled] = useState(false);

    const handleSignatureSuccess = useCallback(() => {
        if (tunnelSubscription?.withCoSubscriber && mainSubscriberHasSigned) {
            // this is the second 'success' event when the co-subscriber signs
            completeCoSubscriptionSigningSession();
        } else {
            // 1. subscriptions without co-subscriber - complete signing session
            // 2. subscriptions with co-subscriber - set status that main subscriber has just signed
            completeSubscriptionSigningSession();
        }
    }, [
        completeCoSubscriptionSigningSession,
        completeSubscriptionSigningSession,
        mainSubscriberHasSigned,
        tunnelSubscription,
    ]);

    const status = useYousign({
        containerId: iframeId,
        signatureUrl: currentSignature?.signerUrl,
        onSuccess: handleSignatureSuccess,
        onError: () => setFrameError(true),
        onCancel: () => setCancelled(true),
    });

    useEffect(() => {
        if (
            (!signatureProcedureInitiated || userStep?.status === stepStatus.INCOMPLETE) &&
            isUninitialized &&
            !loggingOut
        ) {
            getSubscriptionSigningSession();
        }
    }, [
        getSubscriptionSigningSession,
        signatureProcedureInitiated,
        userStep,
        loggingOut,
        isUninitialized,
    ]);

    useEffect(() => {
        if (coSubscriberSuccess && hasFinishedSubscription) {
            dispatch(push('/portfolio'));
        }
    }, [coSubscriberSuccess, dispatch, hasFinishedSubscription]);

    useEffect(() => {
        if (hasFinishedSubscription && completeSuccess && !tunnelSubscription?.withCoSubscriber) {
            dispatch(push('/portfolio'));
        }
    }, [completeSuccess, dispatch, hasFinishedSubscription, tunnelSubscription]);

    useEffect(
        () =>
            setTunnelChangesHandlers(
                afterSubmitCallback => () => afterSubmitCallback(),
                () => true,
                () => signatureProcedureInitiated
            ),
        [signatureProcedureInitiated]
    );

    return (
        <React.Fragment>
            <Loader size={40} loading={status === 'loading'}>
                {cancelled && (
                    <Typography variant="body2" align="center" gutterBottom>
                        {texts.BULLETIN_REFUSEE}
                    </Typography>
                )}
                {subscriberHasSigned && (
                    <Typography variant="body2" align="center" gutterBottom>
                        {texts.BULLETIN_SUCCESS}
                    </Typography>
                )}
            </Loader>
            <Box
                id={iframeId}
                sx={{
                    display:
                        currentSignature && !frameError && !cancelled && !subscriberHasSigned
                            ? 'block'
                            : 'none',
                    width: '100%',
                    height: '595px',
                }}
            ></Box>
            {!internalUser?.impersonatedByUser && !sessionLoader && status !== 'ready' && (
                <SubmitButton text={texts.RETOUR} onClick={handleToggle} />
            )}
        </React.Fragment>
    );
};

export default SubscriptionFormSignature;
